import type { CSSProperties, ReactNode } from 'react';
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@herit-fe/heritage-ui';
import { UI_TEXT } from '@/shared/languages';
import { DataTable } from './data-table';
import type { Column } from './simple-table';
import { useStickyLeftColumns } from './use-sticky-left-columns';
import { useScrollTakeover, useStickyTable } from './use-sticky-table';
import { useTableVirtualRows } from './use-table-virtual-rows';

export interface VirtualSimpleTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (item: T, index: number) => string | number;
  onRowClick?: (item: T) => void;
  /** 행 단위 className (데이터 의존). */
  rowClassName?: (item: T, index: number) => string | undefined;
  /** 외곽 카드 chrome(`DataTable`) 래핑 여부(기본 true) — `SimpleTable` 과 동일. */
  shell?: boolean;
  /**
   * 고정높이 세로 스크롤 + sticky thead/tfoot — `SimpleTable` 과 동일하게 `DataTable` 로 넘긴다.
   * **끄면 스크롤을 레이아웃이 맡는다**(표는 자연 높이로 늘고 페이지가 스크롤) — 그때도 가상화는 그대로 동작한다.
   */
  scroll?: boolean;
  /** `scroll` 일 때 세로 고정높이 상한. 기본은 `DataTable` 기본값(20rem). */
  maxHeight?: number | string;
  /** 합계(tfoot) 행 셀 — 컬럼과 1:1. `SimpleTable` 과 동일. */
  footerCells?: ReactNode[];
  /** 행 높이 추정치(px) — 셀을 크게 그리는 표가 올려 준다. */
  estimateRowHeight?: number;
  /** 뷰포트 밖 선렌더 행 수. */
  overscan?: number;
  /**
   * 모든 행 높이가 같은 표임을 선언 — 행별 실측을 끄고 첫 행 1회 실측으로 대신한다(스크롤 비용 감소).
   * 셀이 전부 한 줄일 때만 켠다 — 문장이 들어가 줄이 늘 수 있는 표에 켜면 스크롤바가 어긋난다.
   */
  uniformRowHeight?: boolean;
  /** 스크롤 엘리먼트 직접 지정(선택) — 자동 탐색이 못 잡는 배치에서만. */
  getScrollElement?: () => HTMLElement | null;
  /**
   * **선두 N개 열을 가로 스크롤에 고정**(0=끔). 열이 많아 옆으로 밀어야 하는 표에서, 행을 식별하는 앞쪽 열
   * (시각·설비명 등)이 화면 밖으로 나가지 않게 한다.
   *
   * ⚠️ **태블릿 이상(`STICKY_MIN_WIDTH`)에서만 동작한다** — 폰은 고정 열이 가용 폭을 크게 먹어
   * (375px 에서 2열이면 40%대) 남는 폭으로 데이터를 못 읽는다. 분기는 컴포넌트 안 한 곳.
   *
   * ⚠️ 세로(sticky 헤더)와 달리 **높이 상한이 필요 없다** — 기준 스크롤포트가 가로를 소유한 표 자신의
   * 뷰포트라, 표가 자연 높이여도 그대로 동작한다.
   */
  stickyLeftCount?: number;
  /**
   * **헤더 행을 화면 상단에 고정**한다. `scroll` 과 함께 쓸 것(끄면 무시).
   *
   * 켜면 `maxHeight` 를 덮어쓴다 — 선택이 아니라 전제다(근거는 `useStickyTable`).
   *
   * ⚠️ `stickyLeftCount` 와 같은 경계(`STICKY_MIN_WIDTH`) — 폰에서 세로를 또 자르면 보이는 행이 몇 줄
   * 안 남고, 페이지 스크롤 안에 작은 스크롤 상자가 하나 더 생겨 조작이 갈린다.
   */
  stickyHeader?: boolean;
}

/**
 * `SimpleTable` 의 **가상화 판** — 뷰포트에 보이는 행만 DOM 에 올린다(`@tanstack/react-virtual`).
 * 컬럼 계약(`Column<T>`)·chrome(`DataTable`)·푸터는 `SimpleTable` 과 동일하므로 **교체가 곧 적용**이다.
 *
 * 왜 별도 컴포넌트인가 — `SimpleTable` 은 대부분의 화면에서 수십 행이라 가상화가 오히려 손해다(스페이서 행·
 * 측정 비용·행 높이 가정이 붙는다). 수천 행이 나올 수 있는 표(계측 결과)만 이걸 쓰고 나머지는 종전대로 둔다.
 *
 * **스페이서 행 방식** — `tbody` 안에서 행을 absolute 로 띄우면 표 레이아웃(열 폭 계산)이 깨지므로, 위아래에
 * 높이만 가진 빈 `tr` 을 넣어 스크롤 길이를 만든다. 그래서 열 폭 자동 계산·sticky 헤더가 그대로 산다.
 *
 * ⚠️ **행 높이가 제각각이면** `measureElement` 로 실측해 보정하지만, 첫 렌더 한 프레임은 추정치로 그린다.
 */
export function VirtualSimpleTable<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  rowClassName,
  shell = true,
  scroll = false,
  maxHeight,
  footerCells,
  estimateRowHeight,
  overscan,
  uniformRowHeight,
  getScrollElement,
  stickyLeftCount = 0,
  stickyHeader = false,
}: VirtualSimpleTableProps<T>) {
  // 폰에선 고정을 끈다(위 prop 주석) — 분기를 여기서 끝내 호출처는 「몇 열을 고정할지」만 정한다.
  const { canStick, boundedHeight } = useStickyTable(stickyHeader, maxHeight);
  const stickyCount = canStick ? stickyLeftCount : 0;
  const { headerRowRef, offsets } = useStickyLeftColumns(stickyCount);
  /**
   * 고정 열의 좌표·레이어 — 오프셋을 아직 못 쟀으면(첫 프레임) **고정을 안 건다**. `left` 없이 sticky 만 걸면
   * 고정 열들이 전부 0 에 겹쳐 붙는다.
   *
   * ⚠️ **z-index 를 클래스가 아니라 인라인으로** 준다. `DataTable` 이 헤더에 거는 `[&_thead_th]:z-20` 은
   * 자손 셀렉터라 특이도가 (0,1,2)인데, 유틸 클래스 `z-30` 은 (0,1,0)이라 **진다**. 그러면 고정된 헤더 2칸이
   * 나머지 헤더와 같은 층이 되고, DOM 순서상 뒤에 오는 셀들이 그 위를 덮어 「안 붙은 것처럼」 보인다.
   * 인라인은 특이도 싸움 밖이라 확실하다.
   */
  const stickyAt = (index: number, head: boolean): CSSProperties | undefined => {
    if (index >= stickyCount || offsets[index] == null) return undefined;
    return {
      left: offsets[index],
      // 헤더 고정 셀(교차점)이 최상단 — 헤더(20) > 본문 고정 셀(10) > 일반 셀 순으로 쌓인다.
      zIndex: head ? 30 : 10,
      // **고정 경계선** — 마지막 고정 열 우측에 세로선. 어디까지 따라오는 열인지 눈으로 갈린다.
      // 보더가 아니라 inset shadow 인 이유: `border-collapse` 표에선 셀 보더가 인접 셀과 병합돼 sticky 시
      // 깨진다(`DataTable` 이 sticky 헤더 구분선에 쓰는 것과 같은 수법).
      // ⚠️ 헤더는 `DataTable` 의 하단 구분선(`inset 0 -1px`)을 **함께 적어야** 한다 — 인라인이 그 클래스를
      // 통째로 덮으므로, 우측선만 쓰면 헤더 밑줄이 이 두 칸에서만 사라진다.
      ...(index === stickyCount - 1 && {
        boxShadow: head
          ? 'inset 0 -1px 0 var(--border), inset -1px 0 0 var(--border)'
          : 'inset -1px 0 0 var(--border)',
      }),
    };
  };
  const stickyClass = (index: number, head: boolean): string | false =>
    index < stickyCount &&
    offsets[index] != null &&
    // 본문 고정 셀엔 **불투명 배경** 필수(밑으로 지나가는 셀이 비치면 안 된다). 헤더는 `DataTable` 이 이미 칠한다.
    cn('sticky', !head && 'bg-card');

  const { bodyRef, indexes, padTop, padBottom, probeHeight, measureRef } = useTableVirtualRows({
    count: data.length,
    estimateRowHeight,
    overscan,
    uniformRowHeight,
    getScrollElement,
    // 상한이 걸린 표는 스크롤러가 자기 뷰포트로 확정 — 인계가 `overflow` 를 토글해도 안 흔들린다.
    ownViewport: stickyHeader,
  });
  // 표가 화면에 다 들어오기 전엔 내부 스크롤을 죽인다(어중간한 인계 방지).
  useScrollTakeover(bodyRef, stickyHeader);

  return (
    <DataTable shell={shell} scroll={scroll} maxHeight={boundedHeight}>
      <TableHeader>
        {/* 고정 열 `left` 오프셋은 이 행의 선두 셀 폭에서 나온다(`useStickyLeftColumns`). */}
        <TableRow ref={headerRowRef}>
          {columns.map((col, index) => (
            <TableHead
              key={col.key}
              className={cn(col.className, stickyClass(index, true))}
              style={stickyAt(index, true)}
            >
              {col.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody ref={bodyRef}>
        {padTop > 0 ? (
          <tr style={{ height: padTop }} aria-hidden>
            <td colSpan={columns.length} />
          </tr>
        ) : null}
        {indexes.map((index) => {
          const item = data[index]!;
          return (
            <TableRow
              key={rowKey(item, index)}
              // 실측 보정 — 행 높이가 추정치와 다르면 이 ref 가 실제 높이를 virtualizer 에 돌려준다.
              ref={measureRef}
              data-index={index}
              className={[onRowClick ? 'cursor-pointer' : '', rowClassName?.(item, index) ?? '']
                .filter(Boolean)
                .join(' ')}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {columns.map((col, colIndex) => (
                // col.className 이 정렬·폭 override. 셀 패딩(행 높이)은 DataTable 토대가 소유.
                <TableCell
                  key={col.key}
                  className={cn(col.className, stickyClass(colIndex, false))}
                  style={stickyAt(colIndex, false)}
                >
                  {col.render
                    ? col.render(item, index)
                    : (((item as Record<string, unknown>)[col.key] as ReactNode) ??
                      UI_TEXT.COMMON_EMPTY_VALUE)}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
        {padBottom > 0 ? (
          <tr style={{ height: padBottom }} aria-hidden>
            <td colSpan={columns.length} />
          </tr>
        ) : null}
        {/* 스크롤러 판정용 프로브 — **폴백에서만** 선다. 행이 하나도 없는 상태로 「넘친다」를 만들어
            `verticalScrollParentOf` 가 진짜 스크롤러를 집게 한다(훅 폴백 분기 주석). 한 프레임짜리다. */}
        {probeHeight > 0 ? (
          <tr style={{ height: probeHeight }} aria-hidden>
            <td colSpan={columns.length} />
          </tr>
        ) : null}
      </TableBody>
      {footerCells ? (
        <TableFooter>
          <TableRow>
            {columns.map((col, colIndex) => (
              <TableCell key={col.key} className={col.className}>
                {footerCells[colIndex]}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      ) : null}
    </DataTable>
  );
}
